import React from "react";
import { AlertTriangle, BadgeCheck, Ban, Search, Trash2 } from "lucide-react";
import { formatDate } from "@/lib/api";
import MemberAvatar from "@/components/common/MemberAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Users tab: search, moderation actions (warn/suspend/ban/activate) and the test-account purge tool.
 *
 * Extracted verbatim from src/pages/Admin.jsx during the code-quality pass:
 * markup, copy, data-testids and behaviour are unchanged - only the file
 * boundary moved.
 */
export default function AdminUserManagement({ users, testAccountCount, q, setQ, busy, searchUsers, purgeTestAccounts, act }) {
  return (
        <div className="mt-6">
          <div className="flex gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A868B]" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchUsers()}
                data-testid="admin-user-search"
                placeholder="Search by name or email"
                className="pl-9 h-10 bg-white border-[#E3DED4]"
              />
            </div>
            <Button onClick={searchUsers} className="bg-[#1F6F78] hover:bg-[#185B62] text-white">
              Search
            </Button>
          </div>

          {testAccountCount > 0 && (
            <div
              data-testid="admin-test-accounts-notice"
              className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-[#E6D3A3] bg-[#FDF6E7] p-4"
            >
              <AlertTriangle className="w-5 h-5 text-[#8A5A2E] shrink-0" />
              <p className="flex-1 text-sm text-[#6B4A22] leading-6">
                <strong>
                  {testAccountCount} automated test account{testAccountCount === 1 ? "" : "s"}
                </strong>{" "}
                found. They are already hidden from the public directory and every sponsor count -
                remove them permanently to tidy up your admin data. Real member accounts are never
                touched.
              </p>
              <Button
                onClick={purgeTestAccounts}
                disabled={busy === "purge-test"}
                data-testid="admin-purge-test-accounts-btn"
                className="bg-[#8A5A2E] hover:bg-[#71481F] text-white shrink-0"
              >
                {busy === "purge-test" ? "Removing..." : "Remove test accounts"}
              </Button>
            </div>
          )}
          <div className="mt-4 sm-card bg-white overflow-x-auto">
            <table data-testid="admin-users-table" className="w-full text-sm">
              <thead className="bg-[#F7F4EF] text-left">
                <tr>
                  {["Member", "Email", "Type", "Status", "Subscription", "Free msgs", "Joined", "Actions"].map((h) => (
                    <th key={h} className="px-3 py-2.5 font-bold text-[#4B5A60] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E3DED4]">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <MemberAvatar user={u} size={30} />
                        <span className="font-semibold text-[#1F2A2E]">{u.display_name}</span>
                        {u.is_admin && (
                          <span className="px-1.5 rounded bg-[#D7EEF0] text-[10px] font-bold text-[#1F6F78]">
                            ADMIN
                          </span>
                        )}
                        {u.is_test_account && (
                          <span
                            data-testid={`admin-user-test-badge-${u.id}`}
                            className="px-1.5 rounded bg-[#FDF0E7] text-[10px] font-bold text-[#8A5A2E]"
                          >
                            TEST
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-[#4B5A60]">{u.email}</td>
                    <td className="px-3 py-2.5">{u.account_type}</td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          u.account_status === "active"
                            ? "bg-[#E4F3EA] text-[#14603C]"
                            : u.account_status === "suspended"
                            ? "bg-[#FBF0DC] text-[#8A5A12]"
                            : "bg-[#FBE6E4] text-[#B42318]"
                        }`}
                      >
                        {u.account_status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">{u.subscription_status}</td>
                    <td className="px-3 py-2.5">{u.free_messages_remaining}</td>
                    <td className="px-3 py-2.5 text-[#7A868B] whitespace-nowrap">
                      {formatDate(u.created_at)}
                    </td>
                    <td className="px-3 py-2.5">
                      {u.is_admin ? (
                        <span className="text-xs text-[#7A868B]">Protected</span>
                      ) : (
                        <div className="flex gap-1">
                          <button
                            onClick={() => act(u.id, "warn")}
                            disabled={busy === u.id + "warn"}
                            data-testid={`warn-${u.id}`}
                            title="Send warning"
                            className="p-1.5 rounded hover:bg-[#FBF0DC]"
                          >
                            <AlertTriangle className="w-4 h-4 text-[#B7791F]" />
                          </button>
                          <button
                            onClick={() => act(u.id, "suspend")}
                            disabled={busy === u.id + "suspend"}
                            data-testid={`suspend-${u.id}`}
                            title="Suspend"
                            className="p-1.5 rounded hover:bg-[#FBE6E4]"
                          >
                            <Ban className="w-4 h-4 text-[#B7791F]" />
                          </button>
                          <button
                            onClick={() => act(u.id, "ban")}
                            disabled={busy === u.id + "ban"}
                            data-testid={`ban-${u.id}`}
                            title="Ban"
                            className="p-1.5 rounded hover:bg-[#FBE6E4]"
                          >
                            <Trash2 className="w-4 h-4 text-[#B42318]" />
                          </button>
                          {u.account_status !== "active" && (
                            <button
                              onClick={() => act(u.id, "activate")}
                              disabled={busy === u.id + "activate"}
                              data-testid={`activate-${u.id}`}
                              title="Reinstate"
                              className="p-1.5 rounded hover:bg-[#E4F3EA]"
                            >
                              <BadgeCheck className="w-4 h-4 text-[#1F7A4D]" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <p className="px-4 py-6 text-sm text-[#4B5A60]">No members found.</p>
            )}
          </div>
        </div>
  );
}
